import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Input, Modal, Space, Tooltip, Typography } from 'antd';

import { DndContext, KeyboardSensor, PointerSensor, pointerWithin, rectIntersection, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
	createToken,
	fromTemplate,
	insertToken,
	moveToken,
	removeToken,
	toTemplate,
	updateText,
	updateTokenFormat,
} from './displayTemplateModel';
import DataTypeBadge from '../common/DataTypeBadge';
import { applyFormatters } from '../../utils/formatters';
import { sampleValueFor } from '../../utils/displaySample';
import { templateTokenInput } from '../../utils/displayTemplate';
import { DEFAULT_FORMAT_RULE_KEY, getFormattingRulesForType } from '../../config/displayTemplateRules';
import { KEBAB } from '../icons';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

const PALETTE_PREFIX = 'palette:';
const CANVAS_ID = 'template-canvas';

// Pointer-first collision so releasing a drag away from any drop target
// cancels it instead of inserting at the nearest droppable.
const collisionDetection = (args) => {
	const within = pointerWithin(args);
	return within.length ? within : rectIntersection(args);
};

const formatLabelFor = (token, element) => {
	if (!token.formatterName) return null;
	const rules = getFormattingRulesForType(element?.dataType);
	const rule = rules.find(r => r.formatterName === token.formatterName);
	return rule?.displayLabel || token.formatterName;
};



//////////////////////////////////////////////////
//
//   TOKEN PILL
//
//////////////////////////////////////////////////

function TokenPill({ token, index, element, onFormatSelect, onRemove }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: token.id });

	const isMissing = !element;
	const label = element?.label || token.elementName || '(unnamed)';
	const formatLabel = formatLabelFor(token, element);

	const rules = getFormattingRulesForType(element?.dataType);
	const menuItems = [
		...rules.map(rule => ({
			key: rule.key,
			label: (
				<div className='template-editor__menu-item'>
					<span>{rule.displayLabel}</span>
					{rule.description ? <span className='template-editor__menu-desc'>{rule.description}</span> : null}
				</div>
			),
		})),
		{ type: 'divider' },
		{ key: '__remove', danger: true, label: 'Remove' },
	];

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
		zIndex: isDragging ? 10 : undefined,
	};

	const pill = (
		<span
			ref={setNodeRef}
			style={style}
			className={'template-editor__pill' + (isMissing ? ' template-editor__pill--missing' : '')}
			data-testid={`template-token-${index}`}
			{...attributes}
			{...listeners}
		>
			{/* A token whose element no longer exists has no type to wear —
			    the pill says so with its own missing treatment. */}
			{element ? <DataTypeBadge dataType={element.dataType} iconOnly className='data-type-badge--bare template-editor__pill-icon' /> : null}
			<span className='template-editor__pill-label'>{label}</span>
			{formatLabel ? <span className='template-editor__pill-format'>{formatLabel}</span> : null}
			<Dropdown
				trigger={['click']}
				menu={{
					items: menuItems,
					selectable: true,
					selectedKeys: [token.formatterName || DEFAULT_FORMAT_RULE_KEY],
					onClick: ({ key, domEvent }) => {
						domEvent?.stopPropagation();
						if (key === '__remove') {
							onRemove(index);
							return;
						}
						onFormatSelect(index, key);
					},
					style: { maxHeight: 320, overflowY: 'auto' },
				}}
			>
				<button
					type='button'
					className='template-editor__pill-menu'
					aria-label={`Options for ${label}`}
					onClick={(e) => e.stopPropagation()}
					onPointerDown={(e) => e.stopPropagation()}
					onKeyDown={(e) => e.stopPropagation()}
				>
					<KEBAB size={14} />
				</button>
			</Dropdown>
		</span>
	);

	return isMissing
		? <Tooltip title={`"${token.elementName}" is not an element of this card`}>{pill}</Tooltip>
		: pill;
}



//////////////////////////////////////////////////
//
//   TEXT SLOT
//
//////////////////////////////////////////////////

function TextSlot({ slotIndex, value, onChange, onKeyDown, onFocusTracked, registerRef }) {
	// Auto-grow via a hidden CSS mirror (::after reads data-value) so the
	// input is always exactly as wide as its content in the real font.
	return (
		<span className='template-editor__slot' data-value={value || ''}>
			<input
				ref={(node) => registerRef(slotIndex, node)}
				className='template-editor__text-input'
				value={value || ''}
				onChange={(e) => onChange(slotIndex, e.target.value)}
				onKeyDown={(e) => onKeyDown(slotIndex, e)}
				onFocus={(e) => onFocusTracked(slotIndex, e.target)}
				onKeyUp={(e) => onFocusTracked(slotIndex, e.target)}
				onClick={(e) => onFocusTracked(slotIndex, e.target)}
				aria-label={`Template text ${slotIndex + 1}`}
				autoComplete='off'
				autoCorrect='off'
				autoCapitalize='off'
				spellCheck={false}
			/>
		</span>
	);
}



//////////////////////////////////////////////////
//
//   PALETTE
//
//////////////////////////////////////////////////

// Droppable canvas lives in its own component so useDroppable can see the
// DndContext provider above it.
function TemplateCanvas({ children, onClick }) {
	const { setNodeRef, isOver } = useDroppable({ id: CANVAS_ID });
	return (
		<div
			className={'template-editor__surface' + (isOver ? ' template-editor__surface--drop' : '')}
			ref={setNodeRef}
			onClick={onClick}
		>
			{children}
		</div>
	);
}

function PaletteChip({ element, onInsert }) {
	const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
		id: PALETTE_PREFIX + element.name,
	});


	return (
		<button
			ref={setNodeRef}
			type='button'
			className='template-editor__palette-pill'
			style={{
				transform: CSS.Translate.toString(transform),
				opacity: isDragging ? 0.6 : 1,
				zIndex: isDragging ? 10 : undefined,
			}}
			onClick={() => onInsert(element)}
			{...attributes}
			{...listeners}
		>
			<DataTypeBadge dataType={element.dataType} iconOnly className='data-type-badge--bare template-editor__pill-icon' />
			<span className='template-editor__palette-label'>{element.label}</span>
			{element.cardName ? <span className='template-editor__palette-sublabel'>{element.cardName}</span> : null}
		</button>
	);
}



//////////////////////////////////////////////////
//
//   EDITOR
//
//////////////////////////////////////////////////

export default function DisplayTemplateEditor({ template, onChange, elements = [] }) {
	const elementMap = useMemo(() => new Map(elements.map(el => [el.name, el])), [elements]);

	const [model, setModel] = useState(() => fromTemplate(template));
	const lastEmittedRef = useRef(template || '');
	const focusRef = useRef({ slot: null, cursor: 0 });
	const pendingFocusRef = useRef(null);
	const inputRefsRef = useRef(new Map());
	const [formatArgsModal, setFormatArgsModal] = useState(null);

	// Adopt external template changes (initial load, save response) without
	// clobbering in-progress edits we emitted ourselves.
	useEffect(() => {
		if ((template || '') === lastEmittedRef.current) return;
		lastEmittedRef.current = template || '';
		setModel(fromTemplate(template));
		focusRef.current = { slot: null, cursor: 0 };
		pendingFocusRef.current = null;
	}, [template]);

	// Every model change serializes and notifies the parent synchronously, so
	// typed text always persists and the form goes dirty immediately.
	const apply = useCallback((next, focusRequest) => {
		setModel(next);
		const serialized = toTemplate(next);
		if (serialized !== lastEmittedRef.current) {
			lastEmittedRef.current = serialized;
			onChange(serialized);
		}
		if (focusRequest) pendingFocusRef.current = focusRequest;
	}, [onChange]);

	useEffect(() => {
		const request = pendingFocusRef.current;
		if (!request) return;
		pendingFocusRef.current = null;
		const node = inputRefsRef.current.get(request.slot);
		if (!node) return;
		node.focus();
		const cursor = Math.min(request.cursor ?? node.value.length, node.value.length);
		try {
			node.setSelectionRange(cursor, cursor);
		} catch (_) { /* number-like inputs */ }
	});

	const registerInputRef = useCallback((slotIndex, node) => {
		if (node) inputRefsRef.current.set(slotIndex, node);
		else inputRefsRef.current.delete(slotIndex);
	}, []);

	const trackFocus = useCallback((slotIndex, node) => {
		focusRef.current = { slot: slotIndex, cursor: node?.selectionStart ?? 0 };
	}, []);

	const handleTextChange = useCallback((slotIndex, value) => {
		focusRef.current = { slot: slotIndex, cursor: inputRefsRef.current.get(slotIndex)?.selectionStart ?? value.length };
		apply(updateText(model, slotIndex, value));
	}, [apply, model]);

	const handleRemoveToken = useCallback((index) => {
		const cursor = (model.texts[index] || '').length;
		apply(removeToken(model, index), { slot: index, cursor });
	}, [apply, model]);

	const handleTextKeyDown = useCallback((slotIndex, event) => {
		const node = event.target;
		const collapsed = node.selectionStart === node.selectionEnd;
		if (event.key === 'Backspace' && collapsed && node.selectionStart === 0 && slotIndex > 0) {
			event.preventDefault();
			handleRemoveToken(slotIndex - 1);
		}
		if (event.key === 'Delete' && collapsed && node.selectionStart === node.value.length && slotIndex < model.tokens.length) {
			event.preventDefault();
			handleRemoveToken(slotIndex);
		}
	}, [handleRemoveToken, model.tokens.length]);

	const insertElement = useCallback((element, slotOverride = null, cursorOverride = null) => {
		const token = createToken({ elementName: element.name });
		const slot = slotOverride ?? focusRef.current.slot ?? model.tokens.length;
		const cursor = slotOverride !== null ? cursorOverride : (focusRef.current.slot !== null ? focusRef.current.cursor : null);
		apply(insertToken(model, slot, token, cursor), { slot: slot + 1, cursor: 0 });
	}, [apply, model]);

	const handleFormatSelect = useCallback((index, ruleKey) => {
		const token = model.tokens[index];
		if (!token) return;
		const element = elementMap.get(token.elementName);
		const rules = getFormattingRulesForType(element?.dataType);
		const rule = rules.find(r => r.key === ruleKey) || rules[0];
		if (rule.args?.length) {
			// Only prefill args when reconfiguring the same transform — a
			// different transform's args would be meaningless here.
			const values = token.formatterName === rule.formatterName ? (token.formatArgs || []) : [];
			setFormatArgsModal({ tokenIndex: index, rule, values });
			return;
		}
		apply(updateTokenFormat(model, index, rule.formatterName, []));
	}, [apply, elementMap, model]);

	const applyFormatArgs = useCallback((tokenIndex, rule, values) => {
		apply(updateTokenFormat(model, tokenIndex, rule.formatterName, values));
	}, [apply, model]);

	// DnD: palette chips drop into the canvas (into the slot nearest the drop
	// point, or at the end); pills reorder among themselves while typed
	// separators stay anchored to their slots. Releasing away from any drop
	// target cancels (pointer-first collision detection). KeyboardSensor makes
	// the sortable a11y announcements on pills truthful.
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	);

	// True when the dragged rect's center passed the hovered pill's center —
	// insert after it rather than before.
	const droppedPastCenter = (event, over) => {
		const activeRect = event.active?.rect?.current?.translated;
		const overRect = over?.rect;
		if (!activeRect || !overRect) return false;
		return (activeRect.left + activeRect.width / 2) > (overRect.left + overRect.width / 2);
	};

	const handleDragEnd = useCallback((event) => {
		const { active, over } = event;
		if (!active || !over) return;
		const activeId = String(active.id);
		if (activeId.startsWith(PALETTE_PREFIX)) {
			const element = elementMap.get(activeId.slice(PALETTE_PREFIX.length));
			if (!element) return;
			const overTokenIndex = model.tokens.findIndex(t => t.id === over.id);
			const slot = overTokenIndex >= 0
				? overTokenIndex + (droppedPastCenter(event, over) ? 1 : 0)
				: model.tokens.length;
			insertElement(element, slot, null);
			return;
		}
		const from = model.tokens.findIndex(t => t.id === active.id);
		if (from < 0) return;
		if (over.id === CANVAS_ID) {
			// Dropped on empty canvas space: move to the end.
			apply(moveToken(model, from, model.tokens.length - 1));
			return;
		}
		if (active.id === over.id) return;
		const to = model.tokens.findIndex(t => t.id === over.id);
		if (to < 0) return;
		apply(moveToken(model, from, to));
	}, [apply, elementMap, insertElement, model]);

	// Clicking canvas whitespace focuses the nearest text slot to the click,
	// caret on the side the click came from.
	const handleCanvasClick = useCallback((event) => {
		if (event.target !== event.currentTarget) return;
		let best = null;
		inputRefsRef.current.forEach((node) => {
			const rect = node.getBoundingClientRect();
			const dx = event.clientX < rect.left ? rect.left - event.clientX : (event.clientX > rect.right ? event.clientX - rect.right : 0);
			const dy = event.clientY < rect.top ? rect.top - event.clientY : (event.clientY > rect.bottom ? event.clientY - rect.bottom : 0);
			const dist = dx * dx + dy * dy;
			if (!best || dist < best.dist) best = { node, dist, rect };
		});
		if (!best) return;
		best.node.focus();
		const caret = event.clientX < best.rect.left ? 0 : best.node.value.length;
		try {
			best.node.setSelectionRange(caret, caret);
		} catch (_) { /* ignore */ }
	}, []);

	// Live example so users see transforms acting on realistic values without
	// leaving the form (e.g. Manufacturer + Truncate(5) previews as 'Manuf…').
	// The date sample is fixed per mount so the preview doesn't shift as they
	// type.
	const sampleNowRef = useRef(new Date().toISOString());
	const preview = useMemo(() => {
		let out = '';
		model.texts.forEach((text, idx) => {
			out += text || '';
			const token = model.tokens[idx];
			if (!token) return;
			const element = elementMap.get(token.elementName);
			const sample = sampleValueFor(element, sampleNowRef.current);
			const specs = token.formatterName ? [{ name: token.formatterName, args: token.formatArgs || [] }] : [];
			// The same pre-formatting rule the saved title uses (a measurement
			// meets a text formatter as its formatted string), so the preview
			// never shows what the title would not.
			const input = templateTokenInput(element, sample, specs);
			out += String(applyFormatters(input, element?.dataType, specs, element?.options) ?? '');
		});
		return out;
	}, [model, elementMap]);

	const tokenIds = model.tokens.map(t => t.id);

	return (
		<div className='template-editor'>
			<DndContext sensors={sensors} collisionDetection={collisionDetection} onDragEnd={handleDragEnd}>
				<TemplateCanvas onClick={handleCanvasClick}>
					<SortableContext items={tokenIds} strategy={rectSortingStrategy}>
						{model.texts.map((text, slotIndex) => (
							<React.Fragment key={`slot-${slotIndex}`}>
								<TextSlot
									slotIndex={slotIndex}
									value={text}
									onChange={handleTextChange}
									onKeyDown={handleTextKeyDown}
									onFocusTracked={trackFocus}
									registerRef={registerInputRef}
								/>
								{model.tokens[slotIndex] ? (
									<TokenPill
										key={model.tokens[slotIndex].id}
										token={model.tokens[slotIndex]}
										index={slotIndex}
										element={elementMap.get(model.tokens[slotIndex].elementName)}
										onFormatSelect={handleFormatSelect}
										onRemove={handleRemoveToken}
									/>
								) : null}
							</React.Fragment>
						))}
					</SortableContext>
				</TemplateCanvas>

				{preview.trim() ? (
					<Typography.Text type='secondary' className='template-editor__preview'>
						Preview: {preview}
					</Typography.Text>
				) : null}

				<div className='template-editor__palette'>
					<Typography.Text strong>Available elements</Typography.Text>
					<Typography.Text type='secondary' className='template-editor__palette-hint'>
						Click or drag an element into the box above, then type any separators around the pills.
					</Typography.Text>
					<div className='template-editor__palette-grid'>
						{elements.map(element => (
							<PaletteChip key={element.name} element={element} onInsert={insertElement} />
						))}
					</div>
				</div>
			</DndContext>

			<FormatArgsModal
				state={formatArgsModal}
				onCancel={() => setFormatArgsModal(null)}
				onSubmit={(values) => {
					if (!formatArgsModal) return;
					applyFormatArgs(formatArgsModal.tokenIndex, formatArgsModal.rule, values);
					setFormatArgsModal(null);
				}}
			/>
		</div>
	);
}



//////////////////////////////////////////////////
//
//   FORMAT ARGS MODAL
//
//////////////////////////////////////////////////

const FormatArgsModal = ({ state, onSubmit, onCancel }) => {
	const [formValues, setFormValues] = useState([]);

	useEffect(() => {
		if (!state?.rule) {
			setFormValues([]);
			return;
		}
		const defaults = state.rule.args?.map((arg, idx) => state.values?.[idx] ?? '') || [];
		setFormValues(defaults);
	}, [state]);

	if (!state?.rule) return null;

	const handleChange = (idx, value) => {
		setFormValues(prev => prev.map((val, i) => (i === idx ? value : val)));
	};

	return (
		<Modal
			title={`Configure ${state.rule.displayLabel}`}
			open={true}
			onOk={() => onSubmit(formValues)}
			onCancel={onCancel}
		>
			<Space direction='vertical' style={{ width: '100%' }}>
				{(state.rule.args || []).map((arg, idx) => (
					<div key={arg.name}>
						<Typography.Text strong>{arg.label}</Typography.Text>
						<Input
							{...NO_TEXT_ASSIST}
							value={formValues[idx] ?? ''}
							onChange={(event) => handleChange(idx, event.target.value)}
							placeholder={arg.placeholder}
						/>
					</div>
				))}
			</Space>
		</Modal>
	);
};
